import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./WatermarkLeftPanelOverlay.css";

/**
 * Full-height overlay docked on the watermark left panel (Color / Position tools).
 */
const WatermarkLeftPanelOverlay = ({
  open = false,
  title = "",
  onClose = () => {},
  panelSelector = "",
  ignoreCloseSelectors = [],
  className = "",
  showCloseButton = true,
  closeOnOutsideClick = true,
  children,
}) => {
  const panelRef = useRef(null);
  const [frame, setFrame] = useState(null);

  const placePanel = useCallback(() => {
    if (!panelSelector) return;
    const el = document.querySelector(panelSelector);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setFrame({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
  }, [panelSelector]);

  useEffect(() => {
    if (!open) return undefined;
    placePanel();
    const onReflow = () => placePanel();
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open, placePanel]);

  useEffect(() => {
    if (!open || !closeOnOutsideClick) return undefined;
    const onDoc = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      for (const sel of ignoreCloseSelectors) {
        if (typeof sel === "string" && e.target.closest?.(sel)) return;
      }
      onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [closeOnOutsideClick, ignoreCloseSelectors, onClose, open]);

  if (!open || !frame) return null;

  return createPortal(
    <div
      ref={panelRef}
      className={`wlp-overlay${className ? ` ${className}` : ""}${
        showCloseButton ? "" : " wlp-overlay--no-header-close"
      }`}
      style={{
        top: `${frame.top}px`,
        left: `${frame.left}px`,
        width: `${frame.width}px`,
        height: `${frame.height}px`,
      }}
      role="dialog"
      aria-label={title}
    >
      <div className="wlp-overlay__header">
        <h2 className="wlp-overlay__title">{title}</h2>
        {showCloseButton ? (
          <button
            type="button"
            className="wlp-overlay__close"
            aria-label={`Close ${title}`}
            onClick={onClose}
          >
            ×
          </button>
        ) : null}
      </div>
      <div className="wlp-overlay__body">{children}</div>
    </div>,
    document.body
  );
};

export default WatermarkLeftPanelOverlay;
